import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, Card, Avatar, Row, Col, Typography, Badge, Tag, Tooltip } from 'antd';
import { 
    UserOutlined, MailOutlined, PhoneOutlined, ShopOutlined, 
    LockOutlined, SafetyOutlined, UserAddOutlined, EditOutlined, 
    PoweroffOutlined, CheckCircleOutlined, InfoCircleOutlined,
    GlobalOutlined
} from '@ant-design/icons';
import axiosInstance from '../../../axiosInstance';
import { BACKENDURLFiles, getUserData } from '../../constants';

const { Option } = Select;
const { Title, Text } = Typography;

const userData = getUserData();
const rolePermission = userData && userData?.user ? userData?.user?.role?.role_permission : {};

const Users = () => {
    const [employees, setEmployees] = useState([]);
    const [shops, setShops] = useState([]);
    const [roles, setRoles] = useState([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingEmployee, setEditingEmployee] = useState(null);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [onlineUsers, setOnlineUsers] = useState([]);

    useEffect(() => {
        fetchEmployees();
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            fetchOnlineUsers();
        }, 15000);
        return () => clearInterval(interval);
    }, [shops]);

    const fetchEmployees = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/employees');
            const { employees = [], shops = [], roles = [] } = response.data;

            const currentUserIsSystemOwner = rolePermission?.is_system_owner === 1 || rolePermission?.is_system_owner === true;

            const filteredEmployees = employees.filter(employee => {
                const roleName = employee.user?.role?.name?.toLowerCase();
                return currentUserIsSystemOwner || roleName !== 'system owner';
            });

            const filteredRoles = roles.filter(role => {
                return currentUserIsSystemOwner || role.name.toLowerCase() !== 'system owner';
            });

            setEmployees(filteredEmployees);
            setShops(shops);
            setRoles(filteredRoles);

            if (shops.length > 0) {
                fetchOnlineUsers(shops[0].id);
            }
        } catch (error) {
            message.error('Failed to fetch employees.');
        } finally {
            setLoading(false);
        }
    };

    const fetchOnlineUsers = async (retailerParam = null) => {
        try {
            const retailer_id = retailerParam || localStorage.getItem('retailerId');
            if (!retailer_id) return;
            const response = await axiosInstance.get(`online-users/retailer/${retailer_id}`);
            const onlineUsersData = response.data.online_users || [];
            setOnlineUsers(onlineUsersData);
        } catch (error) {
            console.error('Failed to fetch online users:', error);
        }
    };

    const showModal = async (employee = null) => {
        setEditingEmployee(employee);
        if (employee) {
            try {
                let shopIds = [];
                try {
                    const response = await axiosInstance.get(`/user-shops/${employee.user.id}`);
                    shopIds = response.data.shops.map(shop => shop.id);
                } catch (error) {
                    console.error('Failed to fetch user shops:', error);
                    shopIds = employee.shop_id ? [employee.shop_id] : [];
                }
                
                form.setFieldsValue({
                    name: employee.user.name,
                    email: employee.user.email,
                    phone: employee.user.phone,
                    role_id: employee.user.role_id,
                    shop_ids: shopIds,
                });
            } catch (error) {
                console.error('Error setting form values:', error);
            }
        } else {
            form.resetFields();
        }
        setIsModalVisible(true);
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            if (editingEmployee) {
                await axiosInstance.put(`/employees/${editingEmployee.user.id}`, values);
                message.success('Employee updated successfully');
            } else {
                await axiosInstance.post('/register', values);
                message.success('Employee added successfully');
            }

            setIsModalVisible(false);
            form.resetFields();
            setEditingEmployee(null);
            fetchEmployees();
        } catch (error) {
            console.error('Error saving employee:', error);
            message.error('Failed to save employee.');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
        setEditingEmployee(null);
    };

    const isUserOnline = (userId) => {
        return onlineUsers.some(user => user.id === userId);
    };

    const changeUserStatus = async (userId, currentStatus) => {
        const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
        try {
            await axiosInstance.post(`/change-user-status/${userId}`, { status: newStatus });
            message.success(`User status changed to ${newStatus}`);
            fetchEmployees();
        } catch (error) {
            console.error('Failed to change user status:', error);
            message.error('Failed to change user status.');
        }
    };

    const columns = [
        {
            title: 'Employee',
            key: 'employee',
            render: (_, record) => {
                const isOnline = isUserOnline(record.user?.id);
                return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Badge dot color={isOnline ? '#52c41a' : '#faad14'} offset={[-4, 32]}>
                            <Avatar
                                src={record.user?.profile_photo_url || ''}
                                size={40}
                                icon={<UserOutlined />}
                                style={{ 
                                    backgroundColor: isOnline ? '#e6f7ff' : '#fff0f6',
                                    color: isOnline ? '#1890ff' : '#eb2f96',
                                    boxShadow: '0 4px 10px rgba(0,0,0,0.06)',
                                    border: `1.5px solid ${isOnline ? '#52c41a' : '#d9d9d9'}`
                                }}
                            />
                        </Badge>
                        <div style={{ marginLeft: 12 }}>
                            <div style={{ fontWeight: '600', color: '#1a1a1a', fontSize: '14px' }}>
                                {record.user?.name}
                            </div>
                        </div>
                    </div>
                );
            },
            width: 200
        },
        {
            title: 'Contact Details',
            key: 'contact',
            render: (_, record) => (
                <div>
                    <div><MailOutlined style={{ marginRight: 6, color: '#bfbfbf' }} />{record.user?.email}</div>
                    <div style={{ marginTop: 4 }}><PhoneOutlined style={{ marginRight: 6, color: '#bfbfbf' }} />{record.user?.phone || 'No phone'}</div>
                </div>
            ),
            width: 220
        },
        {
            title: 'Assigned Shop',
            dataIndex: ['shop', 'name'],
            key: 'shop',
            render: (text) => (
                <span>
                    <ShopOutlined style={{ marginRight: 6, color: '#8c8c8c' }} />
                    <Text strong>{text || 'Main Branch'}</Text>
                </span>
            ),
            width: 180
        },
        {
            title: 'System Role',
            dataIndex: ['user', 'role', 'name'],
            key: 'role',
            render: (text) => (
                <Tag color={text?.toLowerCase() === 'admin' ? 'blue' : 'purple'} style={{ padding: '2px 8px', borderRadius: '4px' }}>
                    {text}
                </Tag>
            ),
            width: 140
        },
        {
            title: 'Status',
            key: 'status',
            render: (_, record) => {
                const isActive = record.user?.status === 'active';
                return (
                    <Tag color={isActive ? 'success' : 'default'} style={{ borderRadius: '12px' }}>
                        {isActive ? 'Active' : 'Inactive'}
                    </Tag>
                );
            },
            width: 100
        },
        {
            title: 'Action',
            key: 'action',
            align: 'center',
            render: (_, record) => {
                const isActive = record.user?.status === 'active';
                return (
                    <Row gutter={8} justify="center">
                        <Col>
                            <Button 
                                type="text"
                                icon={<EditOutlined style={{ color: '#1890ff' }} />}
                                onClick={() => showModal(record)}
                                style={{ borderRadius: '6px' }}
                            >
                                Edit
                            </Button>
                        </Col>
                        <Col>
                            <Button
                                size="small"
                                danger={isActive}
                                type={isActive ? 'text' : 'primary'}
                                icon={<PoweroffOutlined />}
                                onClick={() => changeUserStatus(record.user?.id, record.user?.status)}
                                style={{ borderRadius: '6px' }}
                            >
                                {isActive ? 'Deactivate' : 'Activate'}
                            </Button>
                        </Col>
                    </Row>
                );
            },
            width: 200
        },
    ];

    return (
        <div style={{ padding: '4px' }}>
            <style>
                {`
                .online-dot-pulse {
                    width: 8px;
                    height: 8px;
                    background-color: #52c41a;
                    border-radius: 50%;
                    box-shadow: 0 0 0 0 rgba(82, 196, 26, 0.7);
                    animation: pulseOnline 1.6s infinite cubic-bezier(0.66, 0, 0, 1);
                }
                @keyframes pulseOnline {
                    to {
                        box-shadow: 0 0 0 8px rgba(82, 196, 26, 0);
                    }
                }
                `}
            </style>

            {/* Header Banner */}
            <div className="welcome-banner-card" style={{ marginBottom: '24px' }}>
                <Row justify="space-between" align="middle" gutter={[16, 16]}>
                    <Col xs={24} md={16}>
                        <Title level={2} className="welcome-title" style={{ margin: 0, fontWeight: '800', letterSpacing: '-0.5px' }}>
                            User Directory & Management
                        </Title>
                        <Text className="welcome-subtitle" style={{ fontSize: '15px', display: 'block', marginTop: '6px' }}>
                            Add new employees, assign system roles, associate stores, and track live session status.
                        </Text>
                    </Col>
                    <Col xs={24} md={8} style={{ textAlign: 'right' }}>
                        <Button 
                            type="primary" 
                            size="large" 
                            icon={<UserAddOutlined />} 
                            onClick={() => showModal()}
                            style={{ borderRadius: '8px', boxShadow: '0 4px 10px rgba(24, 144, 255, 0.25)' }}
                        >
                            Add Employee
                        </Button>
                    </Col>
                </Row>
            </div>

            {/* Live Online Users Card */}
            <Card 
                className="dashboard-card" 
                bordered={false} 
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div className="online-dot-pulse" />
                        <span style={{ fontWeight: '700', fontSize: '16px' }}>Live Active Users</span>
                    </div>
                }
                style={{ marginBottom: 24 }}
            >
                <Row gutter={[16, 16]} align="middle">
                    {onlineUsers.length > 0 ? onlineUsers.map((user) => (
                        <Col key={user.id} xs={12} sm={8} md={6} lg={4} xl={3}>
                            <div style={{ 
                                textAlign: 'center', 
                                padding: '16px 8px', 
                                borderRadius: '12px', 
                                background: '#f6ffed', 
                                border: '1px solid #b7eb8f',
                                boxShadow: '0 2px 8px rgba(82, 196, 26, 0.05)'
                            }}>
                                <Badge status="success" offset={[-2, 40]}>
                                    <Avatar
                                        size={56}
                                        src={user.profile_pic_url || ''}
                                        icon={<UserOutlined />}
                                        style={{ border: '2px solid #52c41a', backgroundColor: '#e6f7ff', color: '#1890ff' }}
                                    />
                                </Badge>
                                <div style={{ marginTop: 10, fontWeight: '700', fontSize: '13px', color: '#141414', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {user.name}
                                </div>
                            </div>
                        </Col>
                    )) : (
                        <Col span={24}>
                            <div style={{ textAlign: 'center', padding: '24px 0', color: '#8c8c8c' }}>
                                <InfoCircleOutlined style={{ marginRight: 6 }} /> No other online users currently.
                            </div>
                        </Col>
                    )}
                </Row>
            </Card>

            {/* Users Table Card */}
            <Card className="dashboard-card" bordered={false} bodyStyle={{ padding: '24px 0' }}>
                <Table
                    columns={columns}
                    dataSource={employees}
                    rowKey={(record) => record.user.id}
                    loading={loading}
                    pagination={{ pageSize: 15 }}
                    scroll={{ x: 'max-content' }}
                    style={{ padding: '0 24px' }}
                />
            </Card>

            {/* Add/Edit Modal */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '18px', fontWeight: '700' }}>
                        <UserAddOutlined style={{ color: '#1890ff' }} />
                        <span>{editingEmployee ? 'Edit Employee Details' : 'Add New Employee'}</span>
                    </div>
                }
                visible={isModalVisible}
                onOk={handleOk}
                onCancel={handleCancel}
                okText="Save Details"
                cancelText="Cancel"
                destroyOnClose
                bodyStyle={{ padding: '24px' }}
                okButtonProps={{ loading }}
                style={{ borderRadius: '12px', overflow: 'hidden' }}
            >
                <Form form={form} layout="vertical">
                    <Form.Item label={<Text strong>Full Name</Text>} name="name" rules={[{ required: true, message: 'Please enter name' }]}>
                        <Input prefix={<UserOutlined style={{ color: '#bfbfbf' }} />} placeholder="Enter full name" />
                    </Form.Item>
                    <Form.Item label={<Text strong>Email Address</Text>} name="email" rules={[{ required: true, type: 'email', message: 'Please enter a valid email' }]}>
                        <Input prefix={<MailOutlined style={{ color: '#bfbfbf' }} />} placeholder="Enter email address" />
                    </Form.Item>
                    <Form.Item label={<Text strong>Phone Number</Text>} name="phone" rules={[{ required: true, message: 'Please enter phone number' }]}>
                        <Input prefix={<PhoneOutlined style={{ color: '#bfbfbf' }} />} placeholder="Enter phone number" />
                    </Form.Item>
                    <Form.Item label={<Text strong>System Role</Text>} name="role_id" rules={[{ required: true, message: 'Please select a role' }]}>
                        <Select placeholder="Select organizational role" suffixIcon={<SafetyOutlined />}>
                            {roles.map(role => (
                                <Option key={role.id} value={role.id}>{role.name}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item 
                        label={<Text strong>Assigned Branch Shops</Text>} 
                        name="shop_ids" 
                        rules={[{ required: true, message: 'Please select at least one shop' }]}
                    >
                        <Select mode="multiple" placeholder="Assign to one or more shops" suffixIcon={<ShopOutlined />}>
                            {shops.map(shop => (
                                <Option key={shop.id} value={shop.id}>{shop.name}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default Users;